import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

import AdminLayout from './components/AdminLayout';
import ProblemForm from '../../components/admin/problems/ProblemForm';
import {
  getAllSessions,
  getValidProblemGroupsForSession,
  createProblem,
  Session,
  ProblemGroup,
  Problem
} from '../../services/sessionService';

function ProblemFormPage() {
  const navigate = useNavigate();
  const { sessionId: sessionIdParam } = useParams<{ sessionId?: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<number | ''>(
    sessionIdParam ? parseInt(sessionIdParam, 10) : ''
  );
  const [problemGroups, setProblemGroups] = useState<ProblemGroup[]>([]);
  const [selectedProblemGroup, setSelectedProblemGroup] = useState<ProblemGroup | null>(null);
  
  // Fetch sessions on mount
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoading(true);
        const fetchedSessions = await getAllSessions();
        setSessions(fetchedSessions);
        
        // If there's a session ID in the URL, use that
        if (sessionIdParam) {
          const sessionId = parseInt(sessionIdParam, 10);
          if (!isNaN(sessionId)) {
            setSelectedSessionId(sessionId);
          }
        }
        // If no session ID in URL and there are sessions, select the first one by default
        else if (fetchedSessions.length > 0) {
          setSelectedSessionId(fetchedSessions[0].id);
        }
      } catch (err) {
        console.error('Error fetching sessions:', err);
        setError('Failed to load sessions. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSessions();
  }, [sessionIdParam]);
  
  // Fetch problem groups when session changes
  useEffect(() => {
    const fetchProblemGroups = async () => {
      if (selectedSessionId === '') return;
      
      try {
        setLoading(true);
        const fetchedGroups = await getValidProblemGroupsForSession(selectedSessionId as number);
        setProblemGroups(fetchedGroups);
        
              // Auto-select the first problem group when groups change
        if (fetchedGroups && fetchedGroups.length > 0) {
          // The backend API returns only valid groups for the session
          setSelectedProblemGroup(fetchedGroups[0]);
        }
        
        // Reset error if successful
        setError(null);
      } catch (err) {
        console.error(`Error fetching problem groups for session ${selectedSessionId}:`, err);
        setError('Failed to load problem groups. Please try again.');
        setProblemGroups([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProblemGroups();
  }, [selectedSessionId]);
  
  const handleSessionChange = (event: SelectChangeEvent<number | ''>) => {
    setSelectedSessionId(event.target.value as number | '');
  };
  
  const handleFormSubmit = async (data: Omit<Problem, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!selectedSessionId) {
      setError('Please select a session first');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Ensure sessionId is set correctly
      const problemData = {
        ...data,
        sessionId: selectedSessionId
      };
      
      // Let the backend handle validation of session-group relationship
      await createProblem(problemData);
      setSuccess(true);
      
      // Redirect after short delay
      setTimeout(() => {
        navigate('/admin/sessions');
      }, 1500);
    } catch (err: unknown) {
      console.error('Error creating problem:', err);
      // Extract error message from response if available
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = error.response?.data?.message ||
                          error.message ||
                          'Unknown error occurred';
      setError(`Error creating problem: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };
  
  const goBack = () => {
    // Check if we came from a session page and navigate accordingly
    if (sessionIdParam) {
      // If we came from a specific session, go back to that session's details
      navigate(`/admin/sessions/${sessionIdParam}`);
    } else {
      // Otherwise go to the admin dashboard
      navigate('/admin');
    }
  };
  
  // Show loading spinner while fetching initial data
  if (loading && sessions.length === 0) {
    return (
      <AdminLayout>
        <Container>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        </Container>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <Container maxWidth="lg">
        <Box sx={{ mt: 3, mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
            sx={{ mb: 2 }}
          >
            {sessionIdParam ? 'Back to Session' : 'Back to Dashboard'}
          </Button>
          
          <Typography variant="h4" component="h1" gutterBottom>
            Add New Problem
          </Typography>
          
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Create a new math problem for students to solve.
          </Typography>
          
          {/* Session selection */}
          <Paper sx={{ p: 3, mt: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              1. Select Session and Problem Group
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="session-select-label">Session</InputLabel>
                  <Select
                    labelId="session-select-label"
                    id="session-select"
                    value={selectedSessionId}
                    onChange={handleSessionChange}
                    label="Session"
                    disabled={submitting}
                    error={!selectedSessionId && error !== null}
                  >
                    <MenuItem value="" disabled>
                      <em>Select a session</em>
                    </MenuItem>
                    {sessions.map((session) => (
                      <MenuItem key={session.id} value={session.id}>
                        {session.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Problem created successfully! Redirecting...
              </Alert>
            )}
          </Paper>
          
          {/* Problem form */}
          {loading && selectedSessionId ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading problem groups...
              </Typography>
            </Box>
          ) : selectedSessionId && problemGroups.length > 0 ? (
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  2. Enter Problem Details
                </Typography>
                
                <ProblemForm 
                  problemGroups={problemGroups} 
                  onSubmit={handleFormSubmit}
                  isLoading={submitting}
                  isNew={true}
                  onCancel={goBack}
                  initialData={{
                    sessionId: Number(selectedSessionId),
                    groupId: selectedProblemGroup ? Number(selectedProblemGroup.id) : (problemGroups.length > 0 ? Number(problemGroups[0].id) : 0)
                  }}
                />
              </CardContent>
            </Card>
          ) : selectedSessionId && problemGroups.length === 0 ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This session doesn't have any problem groups. Please create a problem group first.
            </Alert>
          ) : null}
        </Box>
      </Container>
    </AdminLayout>
  );
}

export default ProblemFormPage;